/**
 * Partner acquisition token — SHARED between the marketing site and the portal.
 *
 * A partner distributes ONE link in a mass campaign:
 *
 *     https://sighthost.online/p/<token>
 *
 * marketing  → captures the token from `/p/<token>`, appends `?pt=` to every
 *              portal-bound CTA (they are different origins, so sessionStorage
 *              alone cannot carry it across).
 * portal     → reads `?pt=`, re-captures it, and sends it to the order EF.
 *
 * ── Why this lives in @sighthost/config and not in each app ────────────────
 * Because the two apps MUST agree on the storage key and the accepted token
 * shape. If marketing wrote `sighthost_pt` and the portal read
 * `sighthost_partner_token`, attribution would break silently: the customer
 * completes the journey, the partner earns nothing, and nothing in the data
 * looks wrong. That is the exact failure mode the shared-component doctrine
 * exists to prevent, and it would cost real money for the agreed term.
 * `cookie-consent.ts` in this package is the precedent — same shape of problem.
 *
 * The token is NEVER interpreted client-side. Neither app knows or cares which
 * company it maps to; resolution happens server-side in the Edge Function
 * (architect ruling AR-8). A client passes an opaque string and the SERVER
 * decides what it means — otherwise anyone could mint themselves a long-running
 * revenue share by editing a query string.
 *
 * ── Why sessionStorage and not localStorage ───────────────────────────────
 * This implements CT-8; it is not a storage-convenience choice.
 *
 *   CT-7: a client is linked to the partner when they REQUEST A QUOTE via the link.
 *   CT-8: if they click, do NOT quote, and later arrive directly, they are
 *         attributed to that direct path — the partner earns nothing.
 *
 * sessionStorage dies with the tab, so a visitor who clicks today and returns
 * direct next week genuinely has no token and CT-8 holds by construction.
 * localStorage would quietly persist and attribute that later direct visit —
 * precisely what CT-8 forbids, and invisible until a billing dispute.
 */

/** Storage key. Both apps use this constant — never a literal. */
export const PARTNER_TOKEN_STORAGE_KEY = 'sighthost_pt';

/** Query param carrying the token from marketing to the portal (cross-origin). */
export const PARTNER_TOKEN_QUERY_PARAM = 'pt';

/** Accepted shape, matching the Edge Function resolver: hex, 6–64 chars.
 *  A cheap guard so a probe, a path fragment or junk is never stored. */
const TOKEN_RE = /^[0-9a-f]{6,64}$/;

/** Normalise and shape-check. Returns null for anything not token-shaped. */
function clean(raw: string | null | undefined): string | null {
  const t = (raw ?? '').trim().toLowerCase();
  return TOKEN_RE.test(t) ? t : null;
}

/**
 * Store a token captured from `/p/<token>` or from `?pt=`.
 *
 * No-ops on a malformed token, and in environments without sessionStorage
 * (SSR, prerender, privacy modes). Never throws — a storage failure must cost
 * attribution, never the customer's journey.
 */
export function capturePartnerToken(raw: string | null | undefined): void {
  const token = clean(raw);
  if (!token) return;
  let changed = false;
  try {
    changed = sessionStorage.getItem(PARTNER_TOKEN_STORAGE_KEY) !== token;
    sessionStorage.setItem(PARTNER_TOKEN_STORAGE_KEY, token);
  } catch {
    /* unavailable — attribution lost, journey unharmed */
  }
  if (changed) notifyPartnerTokenListeners();
}

// ── Change subscription (Slice 1C, architect ruling AR-P2) ─────────────────
// The marketing site is prerendered and hydrated. A component that reads the
// token during render produces server HTML without `pt` and client props with
// it, and React 19 keeps the SERVER attribute for the life of the page, so
// every portal CTA silently loses attribution. Components must therefore read
// the token through `useSyncExternalStore(subscribePartnerToken,
// getPartnerToken, () => null)`, which re-renders with the client value right
// after hydration. Writing sessionStorage fires no `storage` event in the same
// tab, so capture notifies explicitly. Kept React-free on purpose: the portal
// (CSR) imports this package too.
const listeners = new Set<() => void>();

function notifyPartnerTokenListeners(): void {
  // Deferred: capture runs inside RootLayout's render body, and notifying a
  // subscribed component synchronously would be a cross-component update
  // during render.
  queueMicrotask(() => {
    for (const listener of listeners) listener();
  });
}

/** Subscribe to token changes. Returns the unsubscribe function. */
export function subscribePartnerToken(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The token for this browsing session, or null. */
export function getPartnerToken(): string | null {
  try {
    return clean(sessionStorage.getItem(PARTNER_TOKEN_STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * Capture from a URL query string if present, then return the session token.
 *
 * Portal entry point: the marketing site hands the token over as `?pt=`, and
 * the portal must grab it on arrival before any redirect or sign-in bounce
 * loses the query string.
 */
export function capturePartnerTokenFromSearch(search: string | URLSearchParams): string | null {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  capturePartnerToken(params.get(PARTNER_TOKEN_QUERY_PARAM));
  return getPartnerToken();
}

/**
 * Capture `?pt=` if present, then return the query string WITHOUT it — or
 * `null` when there was nothing to strip (so a caller can no-op instead of
 * rewriting an unchanged URL and looping).
 *
 * The portal strips the token from its URL after capture: sessionStorage
 * dies with the tab, which is what makes CT-8 hold ("clicked the link, didn't
 * quote, came back direct later → no attribution"). But a portal URL outlives
 * the tab — bookmarked, session-restored, reopened from history — and would
 * re-seed a brand-new session with a visit CT-8 says is not the partner's.
 * Stripping makes the token live in exactly one place with one lifetime.
 *
 * Every other param survives (plan, domain, return, utm_*).
 */
export function captureAndStripPartnerToken(search: string | URLSearchParams): URLSearchParams | null {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  capturePartnerToken(params.get(PARTNER_TOKEN_QUERY_PARAM));
  if (!params.has(PARTNER_TOKEN_QUERY_PARAM)) return null;
  const next = new URLSearchParams(params);
  next.delete(PARTNER_TOKEN_QUERY_PARAM);
  return next;
}
